import { Trace } from '@gitbook/util-debug';
import { arrayOfAll, assertIsDefined, assertNever, type MandateProps } from '@gitbook/util-ts';

import {
    type PageLayoutOptions,
    PageLayoutOptionsCoverMask,
    PageLayoutOptionsCoverSize,
    PageLayoutOptionsSchema,
    PageLayoutOptionsWidth,
    type PageLayoutOptionsWithDefaults,
    PageLayoutPreset,
    type Revision,
    type RevisionPageDocument,
    type RevisionPageDocumentCover,
    type RevisionPageMetaLinkPage,
    RevisionPageType,
} from '../types';

export const PAGE_TITLE_LIMIT = 100;
export const PAGE_DEFAULT_TITLE = 'Untitled';

export const PAGE_DESCRIPTION_LIMIT = 200;

export const PAGE_COVER_HEIGHT_DEFAULT = 240;
export const PAGE_COVER_HEIGHT_MIN = 10;
export const PAGE_COVER_HEIGHT_MAX = 700;

const PRESET_LAYOUT_OPTIONS: Record<PageLayoutPreset, PageLayoutOptionsWithDefaults> = {
    docs: {
        cover: true,
        coverMask: PageLayoutOptionsCoverMask.None,
        coverSize: PageLayoutOptionsCoverSize.Full,
        width: PageLayoutOptionsWidth.Default,
        title: true,
        description: true,
        tableOfContents: true,
        outline: true,
        pagination: true,
        metadata: true,
        tags: true,
        actions: true,
        anchors: true,
    },
    editorial: {
        cover: true,
        coverMask: PageLayoutOptionsCoverMask.None,
        coverSize: PageLayoutOptionsCoverSize.Full,
        width: PageLayoutOptionsWidth.Default,
        title: true,
        description: true,
        tableOfContents: false,
        outline: true,
        pagination: true,
        metadata: true,
        tags: true,
        actions: true,
        anchors: true,
    },
    landing: {
        cover: true,
        coverMask: PageLayoutOptionsCoverMask.None,
        coverSize: PageLayoutOptionsCoverSize.Full,
        width: PageLayoutOptionsWidth.Default,
        title: true,
        description: true,
        tableOfContents: false,
        outline: false,
        pagination: true,
        metadata: false,
        tags: true,
        actions: false,
        anchors: false,
    },
};

function stripLineBreaks(str: string): string {
    return str.replace(/(\r\n|\r|\n)/g, ' ');
}

/**
 * Normalize a page title.
 */
export function normalizePageTitle(title: string, options?: { fallback?: string }): string {
    const defaultTitle =
        typeof options?.fallback === 'string' ? options.fallback : PAGE_DEFAULT_TITLE;
    return stripLineBreaks(title).trim().slice(0, PAGE_TITLE_LIMIT).trim() || defaultTitle;
}

/**
 * Normalize a page description.
 */
export function normalizePageDescription(description: string): string {
    return stripLineBreaks(description).trim().slice(0, PAGE_DESCRIPTION_LIMIT).trim();
}

/**
 * Validate that a layout input value is valid predefined layout preset
 */
export function validatePageLayoutPreset(layout: string): layout is PageLayoutPreset {
    const ALL_PAGE_LAYOUTS = arrayOfAll<PageLayoutPreset>()([
        PageLayoutPreset.Docs,
        PageLayoutPreset.Editorial,
        PageLayoutPreset.Landing,
    ]);
    return ALL_PAGE_LAYOUTS.includes(layout as PageLayoutPreset);
}

/**
 * Validate that a layout object has valid custom layout options.
 * Stored custom layouts can be missing keys added after they were created.
 */
export function validatePageCustomLayout(layout: object): layout is PageLayoutOptions {
    if (typeof layout === 'undefined') {
        return false;
    }

    const result = PageLayoutOptionsSchema.safeParse(layout);

    return result.success && Object.keys(result.data).length > 0;
}

/**
 * Check if a page has a cover.
 */
export function hasPageCover(
    page: RevisionPageDocument
): page is RevisionPageDocument & { cover: MandateProps<RevisionPageDocumentCover, 'yPos'> } {
    return typeof page.cover?.yPos === 'number';
}

/**
 * Get page layout preset or fall back to default.
 * @default BasePageLayoutPreset.Docs
 */
export function getPageLayoutPreset(
    page: Pick<RevisionPageDocument, 'layout'> | null
): PageLayoutPreset | 'custom' {
    if (!page?.layout) {
        return PageLayoutPreset.Docs;
    }

    return typeof page.layout === 'string' && validatePageLayoutPreset(page.layout)
        ? page.layout
        : 'custom';
}

/**
 * Get the layout options currently set for a page.
 */
export function getPageLayoutOptions(
    page: Pick<RevisionPageDocument, 'layout'> | null
): PageLayoutOptionsWithDefaults {
    const layoutPreset = getPageLayoutPreset(page);

    switch (layoutPreset) {
        case PageLayoutPreset.Docs:
            return PRESET_LAYOUT_OPTIONS.docs;
        case PageLayoutPreset.Editorial:
            return PRESET_LAYOUT_OPTIONS.editorial;
        case PageLayoutPreset.Landing:
            return PRESET_LAYOUT_OPTIONS.landing;
        case 'custom':
            return typeof page?.layout === 'object'
                ? normalizeLayoutOptionsWithDefaults(page.layout)
                : PRESET_LAYOUT_OPTIONS.docs;
        default:
            assertNever(layoutPreset);
    }
}

/**
 * Normalize layout options with default values for missing properties.
 */
export function normalizeLayoutOptionsWithDefaults(
    layoutOptions: PageLayoutOptions
): PageLayoutOptionsWithDefaults {
    const defaults: PageLayoutOptionsWithDefaults = {
        width: PageLayoutOptionsWidth.Default,
        cover: true,
        coverMask: PageLayoutOptionsCoverMask.None,
        coverSize: PageLayoutOptionsCoverSize.Full,
        title: true,
        description: true,
        tableOfContents: true,
        outline: true,
        pagination: true,
        metadata: true,
        tags: true,
        actions: true,
        anchors: true,
    };
    // Layouts saved before `anchors` existed take the default of the preset they came from.
    const inferredPreset = inferPageLayoutPreset(layoutOptions);

    return Object.keys(defaults).reduce((acc, key) => {
        // The records are string-keyed at runtime; casting to `Record<string, unknown>` keeps the
        // dynamic access sound while avoiding the `never` target type that results from assigning
        // by a union-typed key.
        const layout = layoutOptions as Record<string, unknown>;
        const accRecord = acc as Record<string, unknown>;
        if (typeof layout[key] === 'undefined') {
            accRecord[key] =
                key === 'anchors'
                    ? inferredPreset
                        ? PRESET_LAYOUT_OPTIONS[inferredPreset].anchors
                        : defaults.anchors
                    : (defaults as Record<string, unknown>)[key];
        } else {
            accRecord[key] = layout[key];
        }
        return acc;
    }, {} as PageLayoutOptionsWithDefaults);
}

/**
 * Guess the preset a custom layout started from, or null if ambiguous.
 */
export function inferPageLayoutPreset(layoutOptions: PageLayoutOptions): PageLayoutPreset | null {
    const differences = (preset: PageLayoutPreset) =>
        Object.entries(layoutOptions).filter(
            ([key, value]) =>
                key !== 'anchors' &&
                value !== undefined &&
                value !== PRESET_LAYOUT_OPTIONS[preset][key as keyof PageLayoutOptions]
        ).length;

    const ranked = (Object.keys(PRESET_LAYOUT_OPTIONS) as PageLayoutPreset[]).sort(
        (a, b) => differences(a) - differences(b)
    );
    return differences(ranked[0]) === differences(ranked[1]) ? null : ranked[0];
}

/**
 * Resolve meta links alternates for all pages in the given content revisions.
 * The goal is to build a graph of all alternates across the given content revisions,
 * and set the resolved alternates for each page accordingly.
 *
 * For ex - if A -> B and A -> C then alternates for A is [B, C], alternates for B is [A, C] and alternates for C is [A, B].
 * Here A, B and C are pages that can be in different contents.
 */
export function resolvePagesAlternates(contentRevisions: Map<string, Pick<Revision, 'pages'>>) {
    const trace = new Trace('resolvePagesAlternates', {
        sampleRate: 0,
    });
    return trace.wrapSync(() => {
        const toNodeKey = (contentId: string, pageId: string): string => `${contentId}:${pageId}`;
        const fromNodeKey = (nodeKey: string): { contentId: string; pageId: string } => {
            const [contentId, pageId] = nodeKey.split(':');
            return { contentId, pageId };
        };

        return traverseAlternatesGraph(
            buildAlternatesGraph(contentRevisions, toNodeKey),
            fromNodeKey
        );
    });
}

/**
 * Build an undirected graph of alternates from the given content revisions.
 * Each node is a page (identified by contentId and pageId) and edges represent alternate links between pages.
 */
function buildAlternatesGraph(
    contentRevisions: Map<string, Pick<Revision, 'pages'>>,
    toNodeKey: (contentId: string, pageId: string) => string
): Map<string, Set<string>> {
    const processedContent = new Set<string>();
    const pendingContent: string[] = [...contentRevisions.keys()];

    const adjacency = new Map<string, Set<string>>();

    const ensureNode = (nodeKey: string) => {
        if (!adjacency.has(nodeKey)) {
            adjacency.set(nodeKey, new Set());
        }
        return nodeKey;
    };

    const addEdge = (fromKey: string, toKey: string) => {
        if (fromKey === toKey) {
            return;
        }

        adjacency.get(ensureNode(fromKey))?.add(toKey);
        adjacency.get(ensureNode(toKey))?.add(fromKey);
    };

    const collectAlternates = (revision: Pick<Revision, 'pages'>, currentContentId: string) => {
        const discoveredContentIds = new Set<string>();

        for (const page of Object.values(revision.pages)) {
            if (page.type !== RevisionPageType.Document) {
                continue;
            }

            const fromPageKey = ensureNode(toNodeKey(currentContentId, page.id));

            for (const alternate of page.metaLinks?.alternates || []) {
                if (alternate.kind !== 'page') {
                    continue;
                }

                const alternateContentId = alternate.space ?? currentContentId;
                const toPageKey = toNodeKey(alternateContentId, alternate.page);
                addEdge(fromPageKey, toPageKey);

                if (alternateContentId !== currentContentId) {
                    discoveredContentIds.add(alternateContentId);
                }
            }
        }

        return discoveredContentIds;
    };

    while (pendingContent.length > 0) {
        const currentContentId = pendingContent.shift();
        if (!currentContentId || processedContent.has(currentContentId)) {
            continue;
        }

        processedContent.add(currentContentId);
        const revision = contentRevisions.get(currentContentId);
        if (!revision) {
            continue;
        }
        const discoveredContentIds = collectAlternates(revision, currentContentId);

        for (const discoveredContentId of discoveredContentIds) {
            if (!processedContent.has(discoveredContentId)) {
                pendingContent.push(discoveredContentId);
            }
        }
    }

    return adjacency;
}

/**
 * Traverse the alternates graph using DFS and resolve alternates for each page.
 * Each page will have alternates set to all other pages in its connected component.
 */
function traverseAlternatesGraph(
    adjacency: Map<string, Set<string>>,
    fromNodeKey: (nodeKey: string) => { contentId: string; pageId: string }
): Record<string, Record<string, Array<RevisionPageMetaLinkPage>>> {
    const alternatesByContentPage: Record<
        string,
        Record<string, Array<RevisionPageMetaLinkPage>>
    > = {};

    const visitedNodes = new Set<string>();

    for (const nodeKey of adjacency.keys()) {
        if (visitedNodes.has(nodeKey)) {
            continue;
        }

        const stack = [nodeKey];
        const component: string[] = [];
        while (stack.length > 0) {
            const current = stack.pop();
            if (!current || visitedNodes.has(current)) {
                continue;
            }

            visitedNodes.add(current);
            component.push(current);

            const neighbors = adjacency.get(current) || new Set<string>();
            for (const neighbor of neighbors) {
                if (!visitedNodes.has(neighbor)) {
                    stack.push(neighbor);
                }
            }
        }

        // For each page in the connected component, set its alternates
        for (const pageNodeKey of component) {
            const { contentId: pageContentId, pageId } = fromNodeKey(pageNodeKey);
            const seen = new Set<string>();
            const alternates: RevisionPageMetaLinkPage[] = [];

            for (const alternateNodeKey of component) {
                if (alternateNodeKey === pageNodeKey) {
                    continue;
                }

                const { contentId: altContentId, pageId: altPageId } =
                    fromNodeKey(alternateNodeKey);
                const alternate: RevisionPageMetaLinkPage = {
                    kind: 'page',
                    page: altPageId,
                    space: altContentId,
                };
                const key = `${alternate.space ?? pageContentId}:${alternate.page}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    alternates.push(alternate);
                }
            }

            if (!alternatesByContentPage[pageContentId]) {
                alternatesByContentPage[pageContentId] = {};
            }

            const contentAlternates = alternatesByContentPage[pageContentId];
            assertIsDefined(contentAlternates);
            contentAlternates[pageId] = alternates;
        }
    }

    return alternatesByContentPage;
}
